import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { differenceInSeconds } from 'date-fns';
import {
  getConversationIdFromRouteParams,
  isAConversationRoute,
  isAInboxViewRoute,
} from 'dashboard/helper/routeHelpers';

const MAX_DISCONNECT_SECONDS = 10800;

// The disconnect delay threshold is added to account for delays in identifying
// disconnections (for example, the websocket disconnection takes up to 3 seconds)
// while fetching the latest updated conversations or messages.
const DISCONNECT_DELAY_THRESHOLD = 15;
const UNKNOWN_INBOX_CHANGES = {
  revokedInboxIds: [],
  grantedInboxIds: [],
  inboxAccessChanged: true,
  authorizationUnknown: true,
};

export const getConversationListQuery = getters => {
  const {
    getAppliedConversationFiltersQuery,
    'customViews/getActiveConversationFolder': activeFolder,
  } = getters;
  if (getAppliedConversationFiltersQuery?.payload?.length) {
    return getAppliedConversationFiltersQuery;
  }
  return activeFolder?.query && Object.keys(activeFolder.query).length
    ? activeFolder.query
    : null;
};

class ReconnectService {
  constructor(store, router) {
    this.store = store;
    this.router = router;
    this.accountId = store.getters.getCurrentAccountId;
    this.disconnectTime = null;
    this.isDisposed = false;

    this.setupEventListeners();
  }

  disconnect = () => {
    this.isDisposed = true;
    this.removeEventListeners();
  };

  isCurrentAccount = () =>
    !this.isDisposed &&
    this.store.getters.getCurrentAccountId === this.accountId;

  setupEventListeners = () => {
    window.addEventListener('online', this.handleOnlineEvent);
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.on(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  removeEventListeners = () => {
    window.removeEventListener('online', this.handleOnlineEvent);
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    emitter.off(BUS_EVENTS.WEBSOCKET_DISCONNECT, this.onDisconnect);
  };

  getSecondsSinceDisconnect = () =>
    this.disconnectTime
      ? Math.max(differenceInSeconds(new Date(), this.disconnectTime), 0)
      : 0;

  // Force reload if the user is disconnected for more than 3 hours
  handleOnlineEvent = () => {
    if (this.getSecondsSinceDisconnect() >= MAX_DISCONNECT_SECONDS) {
      window.location.reload();
    }
  };

  fetchConversations = async () => {
    if (!this.isCurrentAccount()) return;
    await this.store.dispatch('updateChatListFilters', {
      page: null,
      updatedWithin:
        this.getSecondsSinceDisconnect() + DISCONNECT_DELAY_THRESHOLD,
    });
    if (!this.isCurrentAccount()) return;
    await this.store.dispatch('fetchAllConversations');
    if (!this.isCurrentAccount()) return;
    // Reset the updatedWithin in the store chat list filter after fetching conversations when the user is reconnected
    await this.store.dispatch('updateChatListFilters', {
      updatedWithin: null,
    });
  };

  fetchFilteredOrSavedConversations = async queryData => {
    if (!this.isCurrentAccount()) return;
    try {
      await this.store.dispatch('fetchFilteredConversations', {
        queryData,
        page: 1,
      });
    } catch (error) {
      // Ignore error, reconnect flow should continue
    }
  };

  fetchConversationsOnReconnect = async () => {
    if (!this.isCurrentAccount()) return;
    const query = getConversationListQuery(this.store.getters);
    if (query) {
      await this.fetchFilteredOrSavedConversations(query);
    } else {
      await this.fetchConversations();
    }
  };

  fetchConversationMessagesOnReconnect = async () => {
    if (!this.isCurrentAccount()) return;
    const conversationId = getConversationIdFromRouteParams(
      this.router.currentRoute.value.params
    );
    if (conversationId) {
      await this.store.dispatch('syncActiveConversationMessages', {
        conversationId,
      });
    }
  };

  fetchNotificationsOnReconnect = async filter => {
    if (!this.isCurrentAccount()) return;
    await this.store.dispatch('notifications/index', { ...filter, page: 1 });
  };

  revalidateCaches = async () => {
    const previousInboxIds = new Set(
      (this.store.getters['inboxes/getInboxes'] || []).map(({ id }) =>
        Number(id)
      )
    );
    let cacheKeys;
    try {
      cacheKeys = await this.store.dispatch('accounts/getCacheKeys');
    } catch (error) {
      return UNKNOWN_INBOX_CHANGES;
    }
    if (!this.isCurrentAccount()) return UNKNOWN_INBOX_CHANGES;
    const { label, inbox, team } = cacheKeys;
    const [, inboxResult] = await Promise.allSettled([
      this.store.dispatch('labels/revalidate', { newKey: label }),
      this.store.dispatch('inboxes/revalidate', {
        newKey: inbox,
        force: true,
      }),
      this.store.dispatch('teams/revalidate', { newKey: team }),
    ]);
    const refreshedInboxes =
      inboxResult.status === 'fulfilled' ? inboxResult.value : null;
    if (!Array.isArray(refreshedInboxes)) return UNKNOWN_INBOX_CHANGES;
    const currentInboxIds = new Set(
      refreshedInboxes.map(({ id }) => Number(id))
    );
    const revokedInboxIds = [...previousInboxIds].filter(
      id => !currentInboxIds.has(id)
    );
    const grantedInboxIds = [...currentInboxIds].filter(
      id => !previousInboxIds.has(id)
    );
    return {
      revokedInboxIds,
      grantedInboxIds,
      authorizedInboxIds: [...currentInboxIds],
      inboxAccessChanged:
        revokedInboxIds.length > 0 || grantedInboxIds.length > 0,
      authorizationUnknown: false,
    };
  };

  handleRouteSpecificFetch = async () => {
    if (!this.isCurrentAccount()) return;
    const currentRoute = this.router.currentRoute.value.name;
    if (isAConversationRoute(currentRoute, true)) {
      await this.fetchConversationsOnReconnect();
      if (!this.isCurrentAccount()) return;
      await this.fetchConversationMessagesOnReconnect();
    } else if (isAInboxViewRoute(currentRoute, true)) {
      await this.fetchNotificationsOnReconnect(
        this.store.getters['notifications/getNotificationFilters']
      );
      if (!this.isCurrentAccount()) return;
      await this.fetchConversationMessagesOnReconnect();
    }
  };

  setConversationLastMessageId = async () => {
    if (!this.isCurrentAccount()) return;
    const conversationId = getConversationIdFromRouteParams(
      this.router.currentRoute.value.params
    );
    if (conversationId) {
      await this.store.dispatch('setConversationLastMessageId', {
        conversationId,
      });
    }
  };

  onDisconnect = () => {
    this.disconnectTime = new Date();
    this.setConversationLastMessageId();
  };

  onReconnect = async () => {
    let inboxChanges = UNKNOWN_INBOX_CHANGES;
    try {
      inboxChanges = await this.revalidateCaches();
    } catch (error) {
      // Keep the fail-closed fallback so access is still revalidated below.
    }
    if (!this.isCurrentAccount()) return;
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, {
      ...inboxChanges,
      accountId: this.accountId,
    });
    await this.handleRouteSpecificFetch();
  };
}

export default ReconnectService;
